import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import VerifiedIcon from '@mui/icons-material/Verified';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useTranslation } from 'react-i18next';
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx';
import { rowToShooter, readImportRows } from '../../utils/shooterImport';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import { getMatch, getMatches, getMatchesPublic, getShooter, getShooters, getMatchStaff, updateShooter, updateShooterComments, deleteShooter, registerShooter, submitEquipmentCheck, staffCodeAuth, forgotPin } from '../../api';
import TableSortLabel from '@mui/material/TableSortLabel';
import Snackbar from '../../components/Snackbar';
import ShooterForm from '../../components/ShooterForm';
import EquipmentBelt from '../../components/EquipmentBelt';
import { parseEquipmentBelt, getGunInfo } from '../../utils/equipment';
import { getFlagSrc } from '../../utils/scoring';
import { useAuth } from '../../auth/AuthContext';

const EQUIPMENT_VERIFICATIONS = {
  open: ['details', 'openmincaliber', 'mag170', 'maxtorso'],
  standard: ['details', 'mincaliber', 'weightsonbarrel', 'box', 'maxtorso', 'bones', 'nobarrelporting', 'nooptics', 'nocompensators'],
  classic: ['details', 'mincaliber', 'weightsonbarrel', 'box', 'maxtorso', 'bones', 'nooptics', 'nocompensators', 'classichandgun', 'classicmag', 'classicmod', 'classicdustcover'],
  production: ['details', 'mincaliber', 'weightsonbarrel', 'prodtrigger', 'maxbarrellength', 'maxtorso', 'bones', 'nooptics', 'nocompensators', 'inprodlist'],
  'production-optics': ['details', 'mincaliber', 'weightsonbarrel', 'prodtrigger', 'maxbarrellength', 'maxtorso', 'bones', 'mandatoryoptics', 'nocompensators', 'inprodlist'],
  optics: ['details', 'mincaliber', 'weightsonbarrel', 'opticsmag', 'maxtorso', 'bones', 'mandatoryoptics', 'nocompensators'],
  revolver: ['details', 'mincaliber', 'weightsonbarrel', 'notselfloadingrevolver', 'minoronlyrevolver7round'],
  'pcc-optics': ['details'],
  'pcc-iron': ['details'],
};

/**
 * Column labels for extra fields, shared by the shooter export and import.
 */
const getExtraFieldLabels = (t) => ({
  shooter_address: t('shooter.address'),
  shooter_phone: t('shooter.phone'),
  shooter_social_number: t('shooter.socialNumber'),
  shooter_gun_license: t('shooter.gunLicense'),
  shooter_gun_license_exp: t('shooter.gunLicenseExp'),
  gun_make_and_model: t('shooter.gunMakeAndModel'),
  gun_serial: t('shooter.gunSerial'),
  gun_serial_2: t('shooter.gunSerial2', 'Second Gun Serial Number'),
});

export default function Shooters() {
  const { matchId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = useOutletContext();
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.email === 'yoav@endofscoring.com' || permissions?.publicId === 'admin';
  const fileInputRef = useRef(null);
  const [staffPublicIds, setStaffPublicIds] = useState(new Set());
  const [shooters, setShooters] = useState([]);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editShooter, setEditShooter] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cancelAction, setCancelAction] = useState(null); // { id, action: 'approve'|'decline' }
  const [equipChecksShooter, setEquipChecksShooter] = useState(null);
  const [verifyShooter, setVerifyShooter] = useState(null);
  const [verifyChecks, setVerifyChecks] = useState({});
  const [resendingPinId, setResendingPinId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const COLUMNS_STORAGE_KEY = `__shooters_columns_${matchId}`;
  const ALL_COLUMNS = ['publicId', 'name', 'division', 'category', 'club', 'squad', 'status', 'registered'];
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : ALL_COLUMNS;
    } catch { return ALL_COLUMNS; }
  });
  const [colMenuAnchor, setColMenuAnchor] = useState(null);
  const toggleColumn = (col) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col];
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };
  const isColVisible = (col) => visibleColumns.includes(col);
  const [hideCancelled, setHideCancelled] = useState(true);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [importMatchOpen, setImportMatchOpen] = useState(false);
  const [importMatches, setImportMatches] = useState(null);
  const [importMatchId, setImportMatchId] = useState(null);
  const [importShooters, setImportShooters] = useState(null);
  const [importSelected, setImportSelected] = useState(new Set());
  const [importSearch, setImportSearch] = useState('');
  const [importNeedsAuth, setImportNeedsAuth] = useState(false);
  const [importAuthUser, setImportAuthUser] = useState('');
  const [importAuthPass, setImportAuthPass] = useState('');
  const [importAuthError, setImportAuthError] = useState(null);
  const [importAuthLoading, setImportAuthLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [matchRes, shootersRes, staffRes] = await Promise.all([
        getMatch(matchId),
        getShooters(matchId).catch(() => ({ data: [] })),
        getMatchStaff(matchId).catch(() => ({ data: [] })),
      ]);
      setMatch(matchRes.data);
      setShooters(Array.isArray(shootersRes.data) ? shootersRes.data : []);
      const staffData = Array.isArray(staffRes.data) ? staffRes.data : [];
      setStaffPublicIds(new Set(staffData.map((s) => s.publicId)));
    } catch (err) {
      setSnack({ open: true, message: t('shootersPage.loadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !shooters.length) return;
    const target = shooters.find((s) => s.id === editId);
    if (target) {
      setEditShooter(target);
      setEditOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shooters]);

  const squadsMap = {};
  for (const sq of (match?.squads || [])) squadsMap[sq.id] = sq.title;

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const filtered = shooters.filter((s) => {
    if (hideCancelled && s.cancelledParticipation) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) ||
      (s.publicId || '').toString().toLowerCase().includes(q) ||
      (s.division || '').toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q) ||
      (s.club || '').toLowerCase().includes(q);
  }).sort((a, b) => {
    // Cancellation requests always at the top
    const aCr = a.cancellationRequest && !a.cancelledParticipation ? 1 : 0;
    const bCr = b.cancellationRequest && !b.cancelledParticipation ? 1 : 0;
    if (aCr !== bCr) return bCr - aCr;
    const av = (a[sortBy] || '').toString().toLowerCase();
    const bv = (b[sortBy] || '').toString().toLowerCase();
    const cmp = av.localeCompare(bv);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const [editLoading, setEditLoading] = useState(false);

  const handleEdit = async (shooter) => {
    setNotifyEmail(true);
    setNewComment('');
    setEditLoading(true);
    try {
      const res = await getShooter(matchId, shooter.id);
      setEditShooter(res.data);
    } catch {
      setEditShooter({ ...shooter });
    }
    setEditLoading(false);
    setEditOpen(true);
  };

  const handleSaveShooter = async (formData) => {
    setSaving(true);
    try {
      if (editShooter?.id) {
        const data = { ...formData };
        const params = {};
        if (!notifyEmail) params.notifyEmail = 'false';
        await updateShooter(matchId, editShooter.id, data, params);
        // Update in-place instead of reloading entire list
        const res = await getShooter(matchId, editShooter.id);
        const updated = res.data;
        setShooters((prev) => prev.map((s) => s.id === editShooter.id ? updated : s));
      } else {
        const newId = crypto.randomUUID();
        await registerShooter(matchId, { ...formData, id: newId });
        // Fetch the new shooter and append
        try {
          const res = await getShooter(matchId, newId);
          setShooters((prev) => [...prev, res.data]);
        } catch {
          await loadData();
        }
      }
      setEditOpen(false);
      setSnack({ open: true, message: t('common.saveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.detail || err.response?.data?.error || t('common.saveError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !editShooter?.id) return;
    setAddingComment(true);
    try {
      await updateShooterComments(matchId, editShooter.id, { comment: newComment.trim() });
      // Refresh shooter data to get updated comments
      const res = await getShooter(matchId, editShooter.id);
      setEditShooter(res.data);
      setNewComment('');
    } catch (err) {
      setSnack({ open: true, message: t('shootersPage.commentFailed'), severity: 'error' });
    } finally {
      setAddingComment(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await deleteShooter(matchId, id);
      setShooters((prev) => prev.filter((s) => s.id !== id));
      setConfirmDelete(null);
      setSnack({ open: true, message: t('shootersPage.deleted'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('shootersPage.deleteFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelParticipation = async (id) => {
    setSaving(true);
    try {
      await updateShooter(matchId, id, { cancelledParticipation: true, squad: null });
      setShooters((prev) => prev.map((s) => s.id === id ? { ...s, cancelledParticipation: true, squad: null } : s));
      setConfirmCancel(null);
      setSnack({ open: true, message: t('manage.participationCancelled', 'Participation cancelled'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('shootersPage.cancelFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreParticipation = async (id) => {
    setSaving(true);
    try {
      await updateShooter(matchId, id, { cancelledParticipation: false });
      setShooters((prev) => prev.map((s) => s.id === id ? { ...s, cancelledParticipation: false } : s));
      setSnack({ open: true, message: t('manage.participationRestored', 'Participation restored'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('shootersPage.restoreFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenVerify = (shooter) => {
    const existing = shooter.equipmentVerification || {};
    setVerifyChecks({ ...existing });
    setVerifyShooter(shooter);
  };

  const handleSubmitVerify = async () => {
    try {
      await updateShooter(matchId, verifyShooter.id, { verified: true, equipmentVerification: verifyChecks });
      setShooters((prev) => prev.map((s) => s.id === verifyShooter.id ? { ...s, verified: true, equipmentVerification: verifyChecks } : s));
      setVerifyShooter(null);
      setVerifyChecks({});
    } catch (err) {
      setSnack({ open: true, message: t('shootersPage.updateFailed'), severity: 'error' });
    }
  };

  const handleResendPin = async (shooter) => {
    if (!shooter.email) {
      setSnack({ open: true, message: t('shootersPage.resendPinNoEmail', 'This shooter has no email on file'), severity: 'error' });
      return;
    }
    setResendingPinId(shooter.id);
    try {
      await forgotPin(matchId, { publicId: shooter.publicId, email: shooter.email });
      setSnack({ open: true, message: t('shootersPage.resendPinSent', 'PIN sent to {{email}}', { email: shooter.email }), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: t('shootersPage.resendPinFailed', 'Failed to send PIN'), severity: 'error' });
    } finally {
      setResendingPinId(null);
    }
  };

  const handleTogglePaid = async (shooter) => {
    try {
      await updateShooter(matchId, shooter.id, { paid: !shooter.paid });
      setShooters((prev) => prev.map((s) => s.id === shooter.id ? { ...s, paid: !s.paid } : s));
    } catch (err) {
      setSnack({ open: true, message: t('shootersPage.updateFailed'), severity: 'error' });
    }
  };


  // Reads the same columns handleExport writes (ID, Name, Division, Category,
  // Power Factor, Club, Email, Squad, extra fields by label or key), so an
  // exported sheet can be edited and imported back. Failed rows don't stop the rest.
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const rows = await readImportRows(file);
      const extraFieldLabels = getExtraFieldLabels(t);
      const existing = new Set(shooters.map((s) => s.publicId));
      let imported = 0;
      const failures = [];

      for (const row of rows) {
        const parsed = rowToShooter(row, { match, extraFieldLabels });
        if (!parsed) continue;
        if (existing.has(parsed.publicId)) {
          failures.push(`${parsed.name}: ${t('registration.userExists')}`);
          continue;
        }
        const shooter = { id: crypto.randomUUID(), ...parsed };
        try {
          await registerShooter(matchId, shooter);
          existing.add(shooter.publicId);
          imported++;
        } catch (err) {
          failures.push(`${shooter.name}: ${err.response?.data?.detail || err.message}`);
        }
      }

      await loadData();
      if (failures.length > 0) {
        console.warn('Shooter import failures', failures);
        setSnack({
          open: true,
          severity: 'warning',
          message: `${t('shootersPage.importedWithErrors', 'Imported {{imported}}, {{failed}} failed (missing required fields?)', { imported, failed: failures.length })}: ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? '…' : ''}`,
        });
      } else {
        setSnack({ open: true, message: t('shootersPage.imported', { count: imported }), severity: 'success' });
      }
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.detail || t('shootersPage.importFailed'), severity: 'error' });
    }
  };

  const handleExport = () => {
    const extraFieldLabels = getExtraFieldLabels(t);
    const extraFieldKeys = (match?.registrationExtraFields || [])
      .map((f) => typeof f === 'object' ? f.id || f.name : f)
      .filter((f) => f && f !== 'equipment' && f !== 'image' && f !== 'covid');

    const data = shooters.filter((s) => !s.cancelledParticipation).map((s) => {
      const row = {
        ID: s.publicId,
        Name: s.name,
        Division: s.division,
        Category: s.category,
        'Power Factor': s['power-factor'],
        Club: s.club,
        Email: s.email,
        Squad: (match?.squads || []).find((sq) => sq.id === s.squad)?.title || s.squad || '',
        Verified: s.verified ? 'Yes' : 'No',
        Paid: s.paid ? 'Yes' : 'No',
      };
      for (const key of extraFieldKeys) {
        const baseKey = key.replace('_optional', '');
        const label = extraFieldLabels[baseKey] || baseKey;
        row[label] = (s.extraFields || {})[key] || '';
      }
      return row;
    });
    const wb = xlsxUtils.book_new();
    const ws = xlsxUtils.json_to_sheet(data);
    xlsxUtils.book_append_sheet(wb, ws, 'Shooters');
    xlsxWriteFile(wb, `${match?.title || 'match'}_shooters.xlsx`);
  };

  const handleOpenImportMatch = async () => {
    setMoreAnchor(null);
    setImportMatchOpen(true);
    setImportMatchId(null);
    setImportShooters(null);
    setImportSelected(new Set());
    setImportSearch('');
    if (!importMatches) {
      try {
        const res = await getMatchesPublic();
        const all = res.data;
        const list = Object.keys(all).map((k) => ({ ...all[k], id: k, alias: all[k].alias || k }));
        const cc = match?.countryCode;
        setImportMatches(list
          .filter((m) => String(m.id) !== String(matchId) && String(m.alias) !== String(matchId))
          .filter((m) => !cc || m.countryCode === cc)
          .sort((a, b) => {
            const da = a.startDate ? new Date(a.startDate.year, a.startDate.month - 1, a.startDate.day) : 0;
            const db = b.startDate ? new Date(b.startDate.year, b.startDate.month - 1, b.startDate.day) : 0;
            return db - da;
          }));
      } catch { setImportMatches([]); }
    }
  };

  const handleSelectImportMatch = async (mid) => {
    setImportMatchId(mid);
    setImportShooters(null);
    setImportSelected(new Set());
    setImportSearch('');
    setImportNeedsAuth(false);
    setImportAuthError(null);
    setImportAuthUser('');
    setImportAuthPass('');
    try {
      const res = await getShooters(mid);
      const data = res.data || [];
      // Check if we got full data (staff access) by checking if email field exists
      if (data.length > 0 && typeof data[0].email === 'undefined') {
        setImportNeedsAuth(true);
      } else {
        setImportShooters(data);
      }
    } catch { setImportNeedsAuth(true); }
  };

  const handleImportAuth = async () => {
    setImportAuthLoading(true);
    setImportAuthError(null);
    try {
      const { data } = await staffCodeAuth(importMatchId, importAuthPass);
      if (data.accessToken) {
        // Temporarily use this token to fetch shooters
        const res = await getShooters(importMatchId, { auth: data.accessToken });
        setImportShooters(res.data || []);
        setImportNeedsAuth(false);
      }
    } catch {
      setImportAuthError(t('auth.invalidCredentials', 'Invalid credentials'));
    } finally {
      setImportAuthLoading(false);
    }
  };

  const requiredExtraFields = (match?.registrationExtraFields || []).filter(
    (f) => typeof f === 'string' && !f.endsWith('_optional') && f !== 'equipment',
  );

  const shooterMissingFields = (s) => {
    if (!requiredExtraFields.length) return [];
    return requiredExtraFields.filter((f) => {
      const val = (s.extraFields || {})[f];
      return !val || (typeof val === 'string' && !val.trim());
    });
  };

  const handleImportShooters = async () => {
    setImporting(true);
    const existingIds = new Set(shooters.map((s) => s.publicId));
    const toImport = importShooters.filter((s) => importSelected.has(s.id));
    let imported = 0;
    let failed = 0;
    for (const s of toImport) {
      if (existingIds.has(s.publicId)) continue;
      try {
        const data = {
          id: crypto.randomUUID(),
          publicId: s.publicId,
          name: s.name,
          club: s.club || null,
          email: s.email || null,
          division: s.division || 'standard',
          category: s.category || 'regular',
          'power-factor': s['power-factor'] || 'minor',
        };
        if (s.extraFields && Object.keys(s.extraFields).length > 0) {
          data.extraFields = s.extraFields;
        }
        await registerShooter(matchId, data);
        imported++;
      } catch { failed++; }
    }
    await loadData();
    setImportMatchOpen(false);
    const msg = failed > 0
      ? t('shootersPage.importedWithErrors', 'Imported {{imported}}, {{failed}} failed (missing required fields?)', { imported, failed })
      : t('shootersPage.imported', { count: imported });
    setSnack({ open: true, message: msg, severity: failed > 0 ? 'warning' : 'success' });
    setImporting(false);
  };

  const importFilteredShooters = (importShooters || []).filter((s) => {
    if (!importSearch) return true;
    const q = importSearch.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.publicId || '').toLowerCase().includes(q);
  });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{t('matches.shooters')} ({shooters.length})</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={() => { setEditShooter({}); setEditOpen(true); }}>{t('shootersPage.addShooter')}</Button>
          <FormControlLabel
            control={<Checkbox size="small" checked={hideCancelled} onChange={(e) => setHideCancelled(e.target.checked)} />}
            label={<Typography variant="caption">{t('shootersPage.hideCancelled', 'Hide cancelled')}</Typography>}
            sx={{ mr: 1 }}
          />
          <IconButton onClick={(e) => setColMenuAnchor(e.currentTarget)}><ViewColumnIcon /></IconButton>
          <Menu anchorEl={colMenuAnchor} open={Boolean(colMenuAnchor)} onClose={() => setColMenuAnchor(null)}>
            {ALL_COLUMNS.map((col) => (
              <MenuItem key={col} onClick={() => toggleColumn(col)} dense>
                <Checkbox size="small" checked={isColVisible(col)} sx={{ p: 0, mr: 1 }} />
                <ListItemText>{
                  col === 'publicId' ? '#'
                  : col === 'registered' ? t('shootersPage.registered', 'Registered')
                  : col === 'status' ? t('shootersPage.status')
                  : col === 'squad' ? t('matches.squads')
                  : t(`shootersPage.${col}`)
                }</ListItemText>
              </MenuItem>
            ))}
          </Menu>
          <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)}><MoreVertIcon /></IconButton>
          <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
            <MenuItem onClick={() => { setMoreAnchor(null); handleExport(); }}>
              <ListItemText>{t('shootersPage.export')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); fileInputRef.current?.click(); }}>
              <ListItemText>{t('shootersPage.import')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOpenImportMatch}>
              <ListItemText>{t('shootersPage.importFromMatch', 'Import from Another Match')}</ListItemText>
            </MenuItem>
          </Menu>
          <input type="file" ref={fileInputRef} hidden accept=".csv,.xlsx,.xls" onChange={handleImport} />
        </Box>
      </Box>

      <TextField
        placeholder={t('registration.filterByName')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          },
        }}
      />

      {/* Summary */}
      {(() => {
        const active = shooters.filter((s) => !s.cancelledParticipation);
        const paid = active.filter((s) => s.paid).length;
        const squaded = active.filter((s) => s.squad).length;
        const cancelled = shooters.filter((s) => s.cancelledParticipation).length;
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('shootersPage.numActive', 'Listed (Active)')}: {active.length} | {t('shootersPage.numCancelled', 'Cancelled')}: {cancelled} | {t('shootersPage.numPaid', 'Paid')}: {paid} | {t('shootersPage.numSquad', 'Assigned Squads')}: {squaded}
          </Typography>
        );
      })()}

      <TableContainer component={Paper} sx={{ overflowX: 'visible' }}>
        <Table size="small" sx={{ minWidth: 900, bgcolor: 'white' }}>
          <TableHead>
            <TableRow>
              {isColVisible('publicId') && <TableCell><TableSortLabel active={sortBy === 'publicId'} direction={sortBy === 'publicId' ? sortDir : 'asc'} onClick={() => handleSort('publicId')}>#</TableSortLabel></TableCell>}
              {isColVisible('name') && <TableCell><TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>{t('shootersPage.name')}</TableSortLabel></TableCell>}
              {isColVisible('division') && <TableCell><TableSortLabel active={sortBy === 'division'} direction={sortBy === 'division' ? sortDir : 'asc'} onClick={() => handleSort('division')}>{t('shootersPage.division')}</TableSortLabel></TableCell>}
              {isColVisible('category') && <TableCell><TableSortLabel active={sortBy === 'category'} direction={sortBy === 'category' ? sortDir : 'asc'} onClick={() => handleSort('category')}>{t('shootersPage.category')}</TableSortLabel></TableCell>}
              {isColVisible('club') && <TableCell><TableSortLabel active={sortBy === 'club'} direction={sortBy === 'club' ? sortDir : 'asc'} onClick={() => handleSort('club')}>{t('shootersPage.club')}</TableSortLabel></TableCell>}
              {isColVisible('squad') && <TableCell><TableSortLabel active={sortBy === 'squad'} direction={sortBy === 'squad' ? sortDir : 'asc'} onClick={() => handleSort('squad')}>{t('matches.squads')}</TableSortLabel></TableCell>}
              {isColVisible('status') && <TableCell align="center">{t('shootersPage.status')}</TableCell>}
              {isColVisible('registered') && <TableCell><TableSortLabel active={sortBy === 'created'} direction={sortBy === 'created' ? sortDir : 'asc'} onClick={() => handleSort('created')}>{t('shootersPage.registered', 'Registered')}</TableSortLabel></TableCell>}
              <TableCell align="center">{t('shootersPage.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((shooter) => {
              const hasPendingCancel = shooter.cancellationRequest && !shooter.cancelledParticipation;
              return (
              <TableRow key={shooter.id} hover sx={hasPendingCancel ? { bgcolor: '#ffebee' } : undefined}>
                {isColVisible('publicId') && <TableCell>
                  {(() => {
                    const parts = (shooter.publicId || '').split('|');
                    const cc = parts.length > 1 ? parts[0] : null;
                    const num = parts.length > 1 ? parts.slice(1).join('|') : shooter.publicId;
                    const flag = cc ? getFlagSrc({ countryCode: cc, size: 16 }) : null;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {flag && <img src={flag} alt={cc} style={{ width: 16, height: 12 }} />}
                        {num}
                      </Box>
                    );
                  })()}
                </TableCell>}
                {isColVisible('name') && <TableCell>{shooter.name}</TableCell>}
                {isColVisible('division') && <TableCell>{t(`divisions.${(shooter.division || '').replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, shooter.division)}</TableCell>}
                {isColVisible('category') && <TableCell>{t(`categories.${(shooter.category || '').replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, shooter.category)}</TableCell>}
                {isColVisible('club') && <TableCell>{shooter.club}</TableCell>}
                {isColVisible('squad') && <TableCell>{squadsMap[shooter.squad] || shooter.squad || ''}</TableCell>}
                {isColVisible('status') && <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {shooter.cancelledParticipation && <Chip label={t('manage.cancelled', 'Cancelled')} size="small" color="error" variant="outlined" />}
                    {shooter.paid && <Chip icon={<PaymentIcon />} label={t('shootersPage.paid')} size="small" color="info" variant="outlined" />}
                    {(shooter.equipmentChecks || []).some((c) => c?.check === 'error-open' || c?.check === 'error-noscore') && (
                      <Chip icon={<WarningIcon />} label={t('scoring.equipmentError')} size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                </TableCell>}
                {isColVisible('registered') && <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                  {shooter.created ? (<>{new Date(shooter.created).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}<br />{new Date(shooter.created).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false })}</>) : ''}
                </TableCell>}
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {hasPendingCancel ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                        {t('registration.shooterAskedCancel', 'Shooter asked to cancel participation')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" color="error" variant="contained" sx={{ textTransform: 'none' }}
                          disabled={!!cancelAction}
                          onClick={async () => {
                            setCancelAction({ id: shooter.id, action: 'approve' });
                            try {
                              await updateShooter(matchId, shooter.id, { cancelledParticipation: true, cancellationRequest: null, squad: null });
                              setShooters((prev) => prev.map((s) => s.id === shooter.id ? { ...s, cancelledParticipation: true, cancellationRequest: null, squad: null } : s));
                            } finally { setCancelAction(null); }
                          }}>
                          {cancelAction?.id === shooter.id && cancelAction?.action === 'approve' ? <CircularProgress size={16} color="inherit" /> : t('registration.approveCancellation', 'Approve')}
                        </Button>
                        <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                          disabled={!!cancelAction}
                          onClick={async () => {
                            setCancelAction({ id: shooter.id, action: 'decline' });
                            try {
                              await updateShooter(matchId, shooter.id, { cancellationRequest: null }, { reason: 'cancellation-declined' });
                              setShooters((prev) => prev.map((s) => s.id === shooter.id ? { ...s, cancellationRequest: null } : s));
                            } finally { setCancelAction(null); }
                          }}>
                          {cancelAction?.id === shooter.id && cancelAction?.action === 'decline' ? <CircularProgress size={16} /> : t('registration.declineCancellation', 'Decline')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Tooltip title={t('shootersPage.togglePaid', 'Toggle Paid')}>
                        <IconButton size="small" onClick={() => handleTogglePaid(shooter)}>
                          <PaymentIcon fontSize="small" color={shooter.paid ? 'info' : 'disabled'} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shootersPage.equipVerify', 'Equipment Verification')}>
                        <IconButton size="small" onClick={() => handleOpenVerify(shooter)}>
                          <AssignmentTurnedInIcon fontSize="small" color={shooter.verified ? 'success' : 'disabled'} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shootersPage.equipmentCheck', 'Equipment Check')}>
                        <IconButton size="small" onClick={() => setEquipChecksShooter(shooter)}>
                          {(shooter.equipmentChecks || []).some((c) => c?.check === 'error-open' || c?.check === 'error-noscore')
                            ? <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                            : <CheckCircleIcon fontSize="small" color={(shooter.equipmentChecks || []).some(Boolean) ? 'success' : 'disabled'} />
                          }
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={staffPublicIds.has(shooter.publicId) ? t('shootersPage.alreadyStaff', 'Already in staff') : t('shootersPage.addToStaff', 'Add to staff')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/${matchId}/manage/staff`, { state: { addStaff: { publicId: shooter.publicId, name: shooter.name } } })}
                            disabled={staffPublicIds.has(shooter.publicId)}
                          >
                            <BadgeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('shootersPage.editShooter', 'Edit Shooter')}>
                        <IconButton size="small" onClick={() => handleEdit(shooter)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={shooter.email
                        ? t('shootersPage.resendPin', 'Resend PIN to update details')
                        : t('shootersPage.resendPinNoEmail', 'This shooter has no email on file')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleResendPin(shooter)}
                            disabled={!shooter.email || resendingPinId === shooter.id}
                          >
                            {resendingPinId === shooter.id
                              ? <CircularProgress size={16} />
                              : <ForwardToInboxIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {shooter.cancelledParticipation ? (
                        <Tooltip title={t('manage.restoreParticipation', 'Restore Participation')}>
                          <IconButton size="small" onClick={() => handleRestoreParticipation(shooter.id)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('manage.cancelParticipation', 'Cancel Participation')}>
                          <IconButton size="small" onClick={() => setConfirmCancel(shooter.id)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  {isSuperAdmin && (
                    <Tooltip title={t('shootersPage.deleteShooter', 'Delete Shooter')}>
                      <IconButton size="small" onClick={() => setConfirmDelete(shooter.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ); })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">{t('common.noResults')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Loading Dialog */}
      <Dialog open={editLoading} PaperProps={{ sx: { textAlign: 'center', p: 4 } }}>
        <CircularProgress />
      </Dialog>

      {/* Edit Shooter Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editShooter?.id ? t('shootersPage.editShooter') : t('shootersPage.addShooter')}</DialogTitle>
        <DialogContent>
          {editShooter && match && (
            <Box sx={{ mt: 1 }}>
              <ShooterForm
                match={match}
                shooters={shooters}
                editingShooter={editShooter.id ? editShooter : null}
                onSave={handleSaveShooter}
                saving={saving}
                showTos={false}
                noPaper
                gsheetActive={match.registrationLimitGSheet != null}
                submitLabel={t('common.save')}
                beforeSubmit={editShooter.id ? (
                  <FormControlLabel
                    control={<Checkbox checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} />}
                    label={t('registration.notifyEmail')}
                    sx={{ mb: 1 }}
                  />
                ) : null}
              />

              {/* Comments Section */}
              {editShooter.id && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('registration.comments')}</Typography>
                  {(editShooter.comments || []).length > 0 ? (
                    <Box sx={{ mb: 2 }}>
                      {(editShooter.comments || []).map((c, i) => (
                        <Box key={i} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2">{c.comment}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.name || t('manage.staff')} &middot; {new Date(c.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>—</Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={t('registration.comments')}
                      size="small"
                      fullWidth
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                    />
                    <Button variant="outlined" onClick={handleAddComment} disabled={addingComment || !newComment.trim()}>
                      {addingComment ? <CircularProgress size={20} /> : '+'}
                    </Button>
                  </Box>
                </>
              )}

              {/* Equipment Checks Section */}
              {editShooter.id && (editShooter.equipmentChecks || []).some(Boolean) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('shootersPage.equipmentChecks', 'Equipment Checks')}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(match.stages || []).map((_, idx) => {
                      const check = (editShooter.equipmentChecks || [])[idx];
                      if (!check) return (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          {t('results.stageN', { n: idx + 1 })}: —
                        </Typography>
                      );
                      const checkLabel = check.check === 'ok' ? t('shootersPage.equipOk', 'OK')
                        : check.check === 'absent' ? t('scoring.shooterNotHere')
                        : check.check === 'error-open' ? t('scoring.shooterEquipmentFailedMoveToOpen')
                        : check.check === 'error-noscore' ? t('scoring.shooterEquipmentFailedMoveToNoScore')
                        : check.check;
                      const color = check.check === 'ok' ? 'success.main'
                        : check.check === 'absent' ? 'text.secondary'
                        : 'error.main';
                      return (
                        <Typography key={idx} variant="body2">
                          {t('results.stageN', { n: idx + 1 })}:{' '}
                          <Typography component="span" variant="body2" sx={{ color, fontWeight: 600 }}>{checkLabel}</Typography>
                          {check.ro && <Typography component="span" variant="caption" color="text.secondary"> ({check.ro})</Typography>}
                        </Typography>
                      );
                    })}
                  </Box>
                </>
              )}

              {/* Change Log Section */}
              {editShooter.id && (editShooter.log || []).length > 0 && (() => {
                const squadsMap = {};
                for (const sq of (match.squads || [])) squadsMap[sq.id] = sq.title;
                const logKeyLabel = (key) => {
                  const map = {
                    squad: t('manage.squad'),
                    division: t('registration.division'),
                    category: t('registration.category'),
                    'power-factor': t('registration.powerFactor'),
                    name: t('registration.name'),
                    email: t('registration.email'),
                    club: t('registration.club'),
                    team: t('registration.team'),
                    publicId: t('registration.publicId'),
                  };
                  return map[key] || key;
                };
                const formatLogValue = (key, val) => {
                  if (key === 'squad' && val && squadsMap[val]) return squadsMap[val];
                  if (val === null || val === undefined) return '—';
                  return JSON.stringify(val);
                };
                // Expand extraFields into individual sub-field changes
                const expandChanges = (changes) => {
                  const result = [];
                  for (const ch of changes) {
                    if (ch.key === 'extraFields' && typeof ch.pre === 'object' && typeof ch.post === 'object') {
                      const pre = ch.pre || {};
                      const post = ch.post || {};
                      const allKeys = new Set([...Object.keys(pre), ...Object.keys(post)]);
                      for (const k of allKeys) {
                        if (JSON.stringify(pre[k]) !== JSON.stringify(post[k])) {
                          result.push({ key: `extraFields.${k}`, pre: pre[k], post: post[k] });
                        }
                      }
                    } else {
                      result.push(ch);
                    }
                  }
                  return result;
                };
                const filteredLog = [...(editShooter.log || [])].reverse().map((entry) => ({
                  ...entry,
                  changes: expandChanges((entry.changes || []).filter((ch) => JSON.stringify(ch.pre) !== JSON.stringify(ch.post))),
                })).filter((entry) => entry.changes.length > 0);
                if (filteredLog.length === 0) return null;
                return (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('registration.log')}</Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {filteredLog.map((entry, i) => (
                        <Box key={i} sx={{ mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {new Date(entry.modified).toLocaleString()}
                            {entry.reason && ` — ${
                              entry.reason === 'chrono' ? t('registration.changeChrono')
                              : entry.reason === 'shooter-request' ? t('registration.changeShooterRequest')
                              : entry.reason === 'judge-decision' ? t('registration.changeJudgeDecision')
                              : entry.reason
                            }`}
                          </Typography>
                          {entry.changes.map((ch, j) => (
                            <Box key={j} sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', fontFamily: 'monospace', fontSize: '0.8rem', flexDirection: 'row' }}>
                              <span>{logKeyLabel(ch.key)}:</span>
                              <span>{formatLogValue(ch.key, ch.pre)}</span>
                              <span>{i18n.dir() === 'rtl' ? '←' : '→'}</span>
                              <span>{formatLogValue(ch.key, ch.post)}</span>
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </>
                );
              })()}

              {/* Chrono Log Section */}
              {editShooter.id && (editShooter.chronos || []).length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('registration.chronos')}</Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {(editShooter.chronos || []).map((c, i) => (
                      <Box key={i} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2">
                          {c.nextPowerFactor}
                          {c.date && ` — ${new Date(c.date).toLocaleString()}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation (admin only) */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('manage.deleteShooter', 'Delete Shooter?')}</DialogTitle>
        <DialogContent><Typography>{t('manage.deleteShooterConfirm', 'This action cannot be undone.')}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button onClick={() => handleDelete(confirmDelete)} color="error" variant="contained" disabled={saving}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Participation Confirmation */}
      <Dialog open={confirmCancel !== null} onClose={() => setConfirmCancel(null)}>
        <DialogTitle>{t('manage.cancelParticipation', 'Cancel Participation?')}</DialogTitle>
        <DialogContent><Typography>{t('manage.cancelParticipationConfirm', 'The shooter will be marked as cancelled.')}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(null)}>{t('common.cancel')}</Button>
          <Button onClick={() => handleCancelParticipation(confirmCancel)} color="warning" variant="contained" disabled={saving}>{t('common.confirm', 'Confirm')}</Button>
        </DialogActions>
      </Dialog>

      {/* Import from Another Match Dialog */}
      <Dialog open={importMatchOpen} onClose={() => setImportMatchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {importMatchId ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => { setImportMatchId(null); setImportShooters(null); setImportSelected(new Set()); }}>
                <RestoreIcon fontSize="small" />
              </IconButton>
              {t('shootersPage.selectShooters', 'Select Shooters to Import')}
            </Box>
          ) : t('shootersPage.importFromMatch', 'Import from Another Match')}
        </DialogTitle>
        <DialogContent>
          {!importMatchId ? (
            // Step 1: Select a match
            !importMatches ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
            ) : importMatches.length === 0 ? (
              <Typography color="text.secondary">{t('common.noResults')}</Typography>
            ) : (
              <List dense>
                {importMatches.map((m) => (
                  <ListItemButton key={m.id} onClick={() => handleSelectImportMatch(m.id)}>
                    <ListItemText
                      primary={m.title}
                      secondary={m.startDate ? `${m.startDate.day}/${m.startDate.month}/${m.startDate.year}` : ''}
                    />
                  </ListItemButton>
                ))}
              </List>
            )
          ) : importNeedsAuth ? (
            // Step 1.5: Auth needed for the selected match
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('shootersPage.importAuthNeeded', 'You don\'t have access to this match\'s shooters. Enter staff credentials for the selected match.')}
              </Typography>
              <TextField
                label={t('shootersPage.importAuthPassword', 'Staff password')}
                value={importAuthPass}
                onChange={(e) => setImportAuthPass(e.target.value)}
                type="password"
                fullWidth
                size="small"
                onKeyDown={(e) => { if (e.key === 'Enter') handleImportAuth(); }}
              />
              {importAuthError && <Typography variant="body2" color="error">{importAuthError}</Typography>}
              <Button variant="contained" onClick={handleImportAuth} disabled={importAuthLoading || !importAuthPass}>
                {importAuthLoading ? <CircularProgress size={20} /> : t('auth.signIn', 'Sign In')}
              </Button>
            </Box>
          ) : (
            // Step 2: Select shooters
            !importShooters ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
            ) : (
              <>
                <TextField
                  placeholder={t('registration.filterByName')}
                  value={importSearch}
                  onChange={(e) => setImportSearch(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 1 }}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> } }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox
                    size="small"
                    checked={importFilteredShooters.length > 0 && importFilteredShooters.every((s) => importSelected.has(s.id))}
                    indeterminate={importFilteredShooters.some((s) => importSelected.has(s.id)) && !importFilteredShooters.every((s) => importSelected.has(s.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setImportSelected(new Set([...importSelected, ...importFilteredShooters.map((s) => s.id)]));
                      } else {
                        const ids = new Set(importFilteredShooters.map((s) => s.id));
                        setImportSelected(new Set([...importSelected].filter((id) => !ids.has(id))));
                      }
                    }}
                  />
                  <Typography variant="body2">{t('common.selectAll', 'Select All')} ({importFilteredShooters.length})</Typography>
                </Box>
                <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {importFilteredShooters.map((s) => {
                    const alreadyExists = shooters.some((ex) => ex.publicId === s.publicId);
                    const missing = shooterMissingFields(s);
                    return (
                      <ListItemButton
                        key={s.id}
                        disabled={alreadyExists}
                        onClick={() => {
                          const next = new Set(importSelected);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          setImportSelected(next);
                        }}
                      >
                        <Checkbox size="small" checked={importSelected.has(s.id)} disabled={alreadyExists} />
                        <ListItemText
                          primary={`${s.name}${alreadyExists ? ` (${t('shootersPage.alreadyExists', 'already registered')})` : ''}`}
                          secondary={
                            <>
                              {`${s.publicId} · ${s.division || ''}`}
                              {missing.length > 0 && (
                                <Typography component="span" variant="caption" color="warning.main">
                                  {` · ${t('shootersPage.missingFields', 'missing: {{fields}}', { fields: missing.join(', ') })}`}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </>
            )
          )}
        </DialogContent>
        {importMatchId && importShooters && (
          <DialogActions>
            <Button onClick={() => setImportMatchOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              disabled={importSelected.size === 0 || importing}
              onClick={handleImportShooters}
              startIcon={importing ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {t('shootersPage.importCount', 'Import {{count}} Shooters', { count: importSelected.size })}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Equipment Checks Popup */}
      <Dialog open={!!equipChecksShooter} onClose={() => setEquipChecksShooter(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('shootersPage.equipmentChecks', 'Equipment Checks')} - {equipChecksShooter?.name}</DialogTitle>
        <DialogContent>
          {equipChecksShooter && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {(() => {
                const gun = getGunInfo(equipChecksShooter);
                return gun.length ? (
                  <Box sx={{ mb: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {gun.map(({ key, label, value }) => (
                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{t(label)}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'end', wordBreak: 'break-word' }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null;
              })()}
              {(match.stages || []).map((_, idx) => {
                const check = (equipChecksShooter.equipmentChecks || [])[idx];
                if (!check) return (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    {t('results.stageN', { n: idx + 1 })}: —
                  </Typography>
                );
                const checkLabel = check.check === 'ok' ? t('shootersPage.equipOk', 'OK')
                  : check.check === 'absent' ? t('scoring.shooterNotHere')
                  : check.check === 'error-open' ? t('scoring.shooterEquipmentFailedMoveToOpen')
                  : check.check === 'error-noscore' ? t('scoring.shooterEquipmentFailedMoveToNoScore')
                  : check.check;
                const color = check.check === 'ok' ? 'success.main'
                  : check.check === 'absent' ? 'text.secondary'
                  : 'error.main';
                return (
                  <Typography key={idx} variant="body2">
                    {t('results.stageN', { n: idx + 1 })}:{' '}
                    <Typography component="span" variant="body2" sx={{ color, fontWeight: 600 }}>{checkLabel}</Typography>
                    {check.ro && <Typography component="span" variant="caption" color="text.secondary"> ({check.ro})</Typography>}
                  </Typography>
                );
              })}
              {!(match.stages || []).length && (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEquipChecksShooter(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Equipment Verification Dialog */}
      <Dialog open={!!verifyShooter} onClose={() => setVerifyShooter(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {verifyShooter && `${verifyShooter.name} — ${t('shootersPage.equipVerify', 'Equipment Verification')}`}
        </DialogTitle>
        <DialogContent>
          {verifyShooter && (() => {
            const div = (verifyShooter.division || '').toLowerCase();
            const checks = EQUIPMENT_VERIFICATIONS[div] || ['details'];
            const pf = verifyShooter['power-factor'] || 'minor';
            const allChecked = checks.every((c) => verifyChecks[c]);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t(`divisions.${div.replace(/-/g, '')}`, div)} | {t(`registration.${pf}`, pf)}
                </Typography>
                {(() => {
                  const gun = getGunInfo(verifyShooter);
                  return gun.length ? (
                    <Box sx={{ mb: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      {gun.map(({ key, label, value }) => (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{t(label)}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'end', wordBreak: 'break-word' }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null;
                })()}
                {(() => {
                  const belt = parseEquipmentBelt(verifyShooter);
                  return belt ? (
                    <Box sx={{ mb: 1 }}>
                      <EquipmentBelt equipmentObj={belt} readOnly />
                    </Box>
                  ) : null;
                })()}
                {checks.map((check) => {
                  // Some checks have power-factor specific labels
                  const pfKey = `equipVerify.${check}_${pf}`;
                  const baseKey = `equipVerify.${check}`;
                  const label = t(pfKey, '') || t(baseKey, check);
                  return (
                    <FormControlLabel
                      key={check}
                      control={
                        <Checkbox
                          checked={!!verifyChecks[check]}
                          onChange={(e) => setVerifyChecks((prev) => ({ ...prev, [check]: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">{label}</Typography>}
                      sx={{ alignItems: 'flex-start', mx: 0, '& .MuiCheckbox-root': { pt: 0.25 } }}
                    />
                  );
                })}
                {!allChecked && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {t('equipVerify.checkmarks', 'Please check all boxes above')}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyShooter(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmitVerify}
            disabled={(() => {
              if (!verifyShooter) return true;
              const div = (verifyShooter.division || '').toLowerCase();
              const checks = EQUIPMENT_VERIFICATIONS[div] || ['details'];
              return !checks.every((c) => verifyChecks[c]);
            })()}
          >
            {t('equipVerify.submit', 'Verify')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
